import { MAP_INACCURACY_METERS, MapPointTypes } from '@curbnturf/entities';
import { toRadians } from '@curbnturf/helpers';
import { IClusterData } from './cluster-data.interface';
import {
  boondockPinLargeIcon,
  boondockPinSmallIcon,
  poiPinLargeIcon,
  poiPinSmallIcon,
  priceMultiPinFilledIcon,
  priceMultiPinIcon,
  pricePinFilledIcon,
  pricePinIcon,
} from './icons';

const BOONDOCK_COLOR = '#465c50';
const ACTIVITY_COLOR = '#ff7300';
const DUMP_COLOR = '#56235d';
const REST_AREA_COLOR = '#bb6c26';
const WIFI_COLOR = '#009f4e';
const POTABLE_WATER_COLOR = '#08acdc';
const PROPANE_COLOR = '#d00000';

export class CircularTheme {
  scale: number;
  cache: { [id: string]: ImageData } = {};
  iconCache: { [id: string]: H.map.Icon } = {};
  icon: H.map.Icon;
  color: string;

  constructor(scale: number, color: string = '227, 83, 13') {
    this.scale = scale;
    const canvas = document.createElement('canvas');
    const canvasContext = canvas.getContext('2d') || new CanvasRenderingContext2D();
    const size = (canvas.width = canvas.height = 14 * this.scale);
    canvasContext.scale(this.scale, this.scale);
    canvasContext.beginPath();
    canvasContext.strokeStyle = '#FFFFFF';
    canvasContext.fillStyle = color;
    canvasContext.arc(size / 2, size / 2, 6 * this.scale, 0, 2 * Math.PI, !1);
    canvasContext.fill();
    canvasContext.lineWidth = 1;
    canvasContext.stroke();
    this.icon = new H.map.Icon(canvas, {
      size: { w: 14, h: 14 },
      anchor: { x: 7, y: 7 },
    });

    this.color = color;
  }

  canvasCircle(theme: CircularTheme, baseSize: number, color: string) {
    const canvas = document.createElement('canvas');
    const canvasContext: CanvasRenderingContext2D = canvas.getContext('2d') || new CanvasRenderingContext2D();
    const size = baseSize * theme.scale;
    const index = color ? `${size}:${color}` : `${size}:${this.color}`;
    canvas.width = canvas.height = baseSize;
    canvasContext?.scale(theme.scale, theme.scale);

    if (theme.cache[index]) {
      canvasContext?.putImageData(theme.cache[index], 0, 0);
    } else {
      const shapeSize = size / 2;
      canvasContext.beginPath();
      if (color) {
        canvasContext.strokeStyle = `${color}50`;
        canvasContext.fillStyle = `${color}`;
      } else {
        canvasContext.strokeStyle = `rgba(${this.color},0.2)`;
        canvasContext.fillStyle = `rgba(${this.color},1)`;
      }
      canvasContext.arc(shapeSize, shapeSize, (shapeSize / 5) * 3, 0, 2 * Math.PI, !1);
      canvasContext.fill();
      canvasContext.lineWidth = (shapeSize / 5) * 4;
      canvasContext.stroke();
      theme.cache[index] = canvasContext?.getImageData(0, 0, size, size);
    }
    return canvas;
  }

  getClusterPresentation(cluster: H.clustering.ICluster) {
    let clusterColor;
    const types: Record<string, number> = {
      activity: 0,
      dump: 0,
      restArea: 0,
      wifi: 0,
      potableWater: 0,
      propane: 0,
    };

    cluster.forEachDataPoint((point) => {
      const data = point.getData();
      if (data.type === 'poi') {
        types[data.poiType]++;
      }
    });

    let maxValue = 0;
    let highestType = null;

    for (const key in types) {
      if (types[key] > maxValue) {
        maxValue = types[key];
        highestType = key;
      }
    }

    switch (highestType) {
      case 'activity':
        clusterColor = ACTIVITY_COLOR;
        break;
      case 'dump':
        clusterColor = DUMP_COLOR;
        break;
      case 'restArea':
        clusterColor = REST_AREA_COLOR;
        break;
      case 'wifi':
        clusterColor = WIFI_COLOR;
        break;
      case 'potableWater':
        clusterColor = POTABLE_WATER_COLOR;
        break;
      case 'propane':
        clusterColor = PROPANE_COLOR;
        break;
      default:
        clusterColor = BOONDOCK_COLOR;
    }

    const weight = cluster.getWeight();
    let icon = this.iconCache[weight + clusterColor];
    if (!icon) {
      let circle;
      let location;
      if (10 > weight) {
        circle = this.canvasCircle(this, 28, clusterColor);
        location = { x: 11, y: 18 };
      } else if (25 > weight) {
        circle = this.canvasCircle(this, 38, clusterColor);
        location = { x: 13, y: 23 };
      } else if (50 > weight) {
        circle = this.canvasCircle(this, 38, clusterColor);
        location = { x: 13, y: 23 };
      } else if (100 > weight) {
        circle = this.canvasCircle(this, 38, clusterColor);
        location = { x: 13, y: 23 };
      } else if (1e3 > weight) {
        circle = this.canvasCircle(this, 48, clusterColor);
        location = { x: 15, y: 28 };
      } else {
        circle = this.canvasCircle(this, 66, clusterColor);
        location = { x: 20, y: 38 };
      }

      const size = circle.width / this.scale;
      const canvasContext = circle.getContext('2d') || new CanvasRenderingContext2D();

      const fontSize = 11 * this.scale;
      canvasContext.font = `bold ${fontSize}px Arial`;
      const x = location.x;
      const y = location.y;
      canvasContext.fillStyle = '#FFFFFF';
      canvasContext.fillText(`${weight}`, x, y);

      icon = new H.map.Icon(circle, {
        anchor: { x: size / 2, y: size / 2 },
      });
      this.iconCache[weight + clusterColor] = icon;
    }

    const data: any[] = [];
    cluster.forEachDataPoint((datum) => {
      data.push(datum.getData());
    });

    const marker = new H.map.Marker(cluster.getPosition(), {
      icon,
      min: cluster.getMinZoom(),
      max: cluster.getMaxZoom(),
      data,
    });
    marker.setData(cluster);
    return marker;
  }
}

export class IconMarkerTheme {
  boondockIcon = new H.map.Icon(boondockPinSmallIcon('#465d50'));
  hostIconCache: { [id: number]: H.map.Icon } = {};

  selectedBoondockIcon = new H.map.Icon(boondockPinLargeIcon('#465d50'));
  selectedHostIconCache: { [id: number]: H.map.Icon } = {};

  poiIcon = {
    empty: new H.map.Icon(poiPinSmallIcon('empty')),
    dump: new H.map.Icon(poiPinSmallIcon('dump', DUMP_COLOR)),
    restArea: new H.map.Icon(poiPinSmallIcon('restArea', REST_AREA_COLOR)),
    wifi: new H.map.Icon(poiPinSmallIcon('wifi', WIFI_COLOR)),
    potableWater: new H.map.Icon(poiPinSmallIcon('potableWater', POTABLE_WATER_COLOR)),
    propane: new H.map.Icon(poiPinSmallIcon('propane', PROPANE_COLOR)),
    biking: new H.map.Icon(poiPinSmallIcon('biking')),
    boating: new H.map.Icon(poiPinSmallIcon('boating')),
    fishing: new H.map.Icon(poiPinSmallIcon('fishing')),
    golf: new H.map.Icon(poiPinSmallIcon('golf')),
    hiking: new H.map.Icon(poiPinSmallIcon('hiking')),
    horseback: new H.map.Icon(poiPinSmallIcon('horseback')),
    hunting: new H.map.Icon(poiPinSmallIcon('hunting')),
    kayaking: new H.map.Icon(poiPinSmallIcon('kayaking')),
    motorSports: new H.map.Icon(poiPinSmallIcon('motorSports')),
    ohv: new H.map.Icon(poiPinSmallIcon('ohv')),
    rafting: new H.map.Icon(poiPinSmallIcon('rafting')),
    rockClimbing: new H.map.Icon(poiPinSmallIcon('rockClimbing')),
    snowSports: new H.map.Icon(poiPinSmallIcon('snowSports')),
    surfing: new H.map.Icon(poiPinSmallIcon('surfing')),
    swimming: new H.map.Icon(poiPinSmallIcon('swimming')),
    waterSports: new H.map.Icon(poiPinSmallIcon('waterSports')),
    wildlifeWatching: new H.map.Icon(poiPinSmallIcon('wildlifeWatching')),
    windSports: new H.map.Icon(poiPinSmallIcon('windSports')),
    custom: new H.map.Icon(poiPinSmallIcon('custom')),
  };

  selectedPoiIcon = {
    empty: new H.map.Icon(poiPinLargeIcon('empty')),
    dump: new H.map.Icon(poiPinLargeIcon('dump', DUMP_COLOR)),
    restArea: new H.map.Icon(poiPinLargeIcon('restArea', REST_AREA_COLOR)),
    wifi: new H.map.Icon(poiPinLargeIcon('wifi', WIFI_COLOR)),
    potableWater: new H.map.Icon(poiPinLargeIcon('potableWater', POTABLE_WATER_COLOR)),
    propane: new H.map.Icon(poiPinLargeIcon('propane', PROPANE_COLOR)),
    biking: new H.map.Icon(poiPinLargeIcon('biking')),
    boating: new H.map.Icon(poiPinLargeIcon('boating')),
    fishing: new H.map.Icon(poiPinLargeIcon('fishing')),
    golf: new H.map.Icon(poiPinLargeIcon('golf')),
    hiking: new H.map.Icon(poiPinLargeIcon('hiking')),
    horseback: new H.map.Icon(poiPinLargeIcon('horseback')),
    hunting: new H.map.Icon(poiPinLargeIcon('hunting')),
    kayaking: new H.map.Icon(poiPinLargeIcon('kayaking')),
    motorSports: new H.map.Icon(poiPinLargeIcon('motorSports')),
    ohv: new H.map.Icon(poiPinLargeIcon('ohv')),
    rafting: new H.map.Icon(poiPinLargeIcon('rafting')),
    rockClimbing: new H.map.Icon(poiPinLargeIcon('rockClimbing')),
    snowSports: new H.map.Icon(poiPinLargeIcon('snowSports')),
    surfing: new H.map.Icon(poiPinLargeIcon('surfing')),
    swimming: new H.map.Icon(poiPinLargeIcon('swimming')),
    waterSports: new H.map.Icon(poiPinLargeIcon('waterSports')),
    wildlifeWatching: new H.map.Icon(poiPinLargeIcon('wildlifeWatching')),
    windSports: new H.map.Icon(poiPinLargeIcon('windSports')),
    custom: new H.map.Icon(poiPinLargeIcon('custom')),
  };

  getIcon(data: IClusterData): H.map.Icon {
    if (data.type === MapPointTypes.poi) {
      return this.poiIcon[data.poiType || 'empty'];
    } else if (!data.price) {
      return this.boondockIcon;
    }

    const price = Math.round(data.price || 0);
    if (!this.hostIconCache[price]) {
      if (data.multisite) {
        this.hostIconCache[price] = new H.map.Icon(priceMultiPinIcon(price));
      } else {
        this.hostIconCache[price] = new H.map.Icon(pricePinIcon(price));
      }
    }

    return this.hostIconCache[price];
  }

  getSelectedIcon(data: IClusterData): H.map.Icon {
    if (data.type === MapPointTypes.poi) {
      return this.selectedPoiIcon[data.poiType || 'empty'];
    } else if (!data.price) {
      return this.selectedBoondockIcon;
    }

    const price = Math.round(data.price || 0);
    if (!this.selectedHostIconCache[price]) {
      if (data.multisite) {
        this.selectedHostIconCache[price] = new H.map.Icon(priceMultiPinFilledIcon(price));
      } else {
        this.selectedHostIconCache[price] = new H.map.Icon(pricePinFilledIcon(price));
      }
    }

    return this.selectedHostIconCache[price];
  }

  generateClusterNoiseThemeFunction(
    iconMarkerTheme: IconMarkerTheme,
    hostInaccuracyGroup?: H.map.Group,
  ): (noisePoint: H.clustering.INoisePoint) => H.map.Marker {
    return function clusterNoiseThemeFunction(noisePoint: H.clustering.INoisePoint): H.map.Marker {
      // Get a reference to data object our noise points
      const data: IClusterData = noisePoint.getData();

      // Create a marker for the noisePoint
      const noiseMarker = new H.map.Marker(noisePoint.getPosition(), {
        // Use min zoom from a noise point
        // to show it correctly at certain zoom levels:
        min: noisePoint.getMinZoom(),
        icon: data.selected ? iconMarkerTheme.getSelectedIcon(data) : iconMarkerTheme.getIcon(data),
        data,
      });

      noiseMarker.setData(data);

      if (data.type === MapPointTypes.standard && hostInaccuracyGroup) {
        const coordinates = noisePoint.getPosition();

        if (
          !hostInaccuracyGroup
            .getObjects()
            .find(
              (circle) =>
                circle?.getData()?.coordinates?.lat === coordinates?.lat &&
                circle?.getData()?.coordinates?.lng === coordinates?.lng,
            )
        ) {
          hostInaccuracyGroup.addObject(
            new H.map.Circle(coordinates, MAP_INACCURACY_METERS, {
              style: new H.map.SpatialStyle({
                strokeColor: 'rgba(227, 83, 13, 0.3)',
                lineWidth: 0,
                fillColor: 'rgba(227, 83, 13, 0.2)',
              }),
              data: { coordinates, type: 'noise' },
            }),
          );
        }
      }

      return noiseMarker;
    };
  }
}

export function multiPinCoordinates(
  map: H.Map,
  origin: H.geo.Point,
  index: number,
  length: number,
  iconOffset: number = 10,
): H.geo.Point {
  if (!length || !map) {
    return origin;
  }

  // standard calculation if less then 12
  // in degrees
  let angle = (360 / length) * index;
  // in pixels
  let distance = 15 + 2.5 * length;

  if (length >= 6) {
    // these calculations work until at least 50 anything beyond that may need adjustment
    angle = (index * (60 - Math.min(-5 + 24 * Math.max(Math.log1p(index) / Math.log(8), 0), 50))) % 360;
    distance = 30 + index * 5 - Math.min(0.0375 * index * index, index * 2.25);
  }

  const originXY = map.geoToScreen(origin);
  if (!originXY) {
    return origin;
  }

  const x = originXY.x + distance * Math.cos(toRadians(angle));
  const y = originXY.y + distance * Math.sin(toRadians(angle)) + iconOffset;

  return map.screenToGeo(x, y) || origin;
}
